/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import {
  addMonths,
  format,
  getYear,
  parseISO,
  setMonth,
  setYear,
  subMonths,
} from "date-fns";
import { pt } from "date-fns/locale";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { cn } from "@/lib/utils";

interface CustomDatePickerProps {
  date: string | undefined;
  onDateChange: (date: Date | undefined) => void;
  row: any;
  control: any;
  isLoading: boolean;
}

export default function DatePickerCell({
  date,
  onDateChange,
  isLoading,
  row,
  control,
}: CustomDatePickerProps) {
  const [view, setView] = useState<"default" | "year" | "month" | "day">(
    "default"
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [datePicker, setDatePicker] = useState<Date | undefined>(undefined);

  const watchedDate = useWatch({
    control,
    name: `divergence_${row.original.id}.has_dependency`,
  });

  useEffect(() => {
    if (date) {
      const parsedDate = parseISO(date);
      setCurrentDate(parsedDate);
      setDatePicker(parsedDate);
    }
  }, [date]);

  const years = Array.from(
    { length: 5 },
    (_, i) => getYear(new Date()) - 2 + i
  );
  const months = Array.from({ length: 12 }, (_, i) => i);

  const handleMonthNavigation = (increment: number) => {
    setCurrentDate((prevDate) =>
      increment > 0 ? addMonths(prevDate, 1) : subMonths(prevDate, 1)
    );
  };

  const handleYearSelection = (year: number) => {
    setCurrentDate((prevDate) => setYear(prevDate, year));
    setView("month");
  };

  const handleMonthSelection = (month: number) => {
    setCurrentDate((prevDate) => setMonth(prevDate, month));
    setView("day");
  };

  const handleDateChange = (selectedDate: Date | undefined) => {
    onDateChange(selectedDate);
    setDatePicker(selectedDate);
    setIsPopoverOpen(false);
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        {!watchedDate ? (
          <Tooltip delayDuration={1}>
            <TooltipTrigger asChild>
              <div className="w-full">
                <Button
                  variant="secondary"
                  className={cn(
                    "w-full justify-between text-left font-normal",
                    !datePicker && "text-muted-foreground"
                  )}
                  disabled
                >
                  {datePicker &&
                    !isLoading &&
                    format(datePicker, "dd/MM/yyyy", { locale: pt })}
                  {!datePicker && !isLoading && <span>Selecione uma data</span>}
                  {isLoading && (
                    <svg
                      className="animate-spin h-5 w-5 text-[#2B0C1A]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-[#141414] text-white text-center">
              <span>
                Para adicionar uma data é <br />
                necessario ter um dependência
              </span>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="outline"
            className={cn(
              "w-full justify-between text-left font-normal",
              !datePicker && "text-muted-foreground"
            )}
          >
            {datePicker &&
              !isLoading &&
              format(datePicker, "dd/MM/yyyy", { locale: pt })}
            {!datePicker && !isLoading && <span>Selecione uma data</span>}
            {isLoading && (
              <svg
                className="animate-spin h-5 w-5 text-[#2B0C1A]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 border-none pointer-events-auto" align="start">
        <div className="w-full">
          <div className="flex items-center justify-between p-2">
            <Button
              variant="ghost"
              className="h-7 w-7 p-0 bg-white text-black hover:bg-gray-100"
              onClick={() => handleMonthNavigation(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex space-x-1">
              <Button
                variant="ghost"
                className="bg-white text-black hover:bg-gray-100"
                onClick={() => setView("month")}
              >
                {format(currentDate, "MMMM", { locale: pt })}
              </Button>
              <Button
                variant="ghost"
                className="bg-white text-black hover:bg-gray-100"
                onClick={() => setView("year")}
              >
                {format(currentDate, "yyyy", { locale: pt })}
              </Button>
            </div>
            <Button
              variant="ghost"
              className="h-7 w-7 p-0 bg-white text-black hover:bg-gray-100"
              onClick={() => handleMonthNavigation(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {view === "year" && (
          <div className="grid grid-cols-4 gap-2 p-3">
            {years.map((year) => (
              <Button
                key={year}
                variant="ghost"
                className="w-full bg-white text-black hover:bg-gray-100"
                onClick={() => handleYearSelection(year)}
              >
                {year}
              </Button>
            ))}
          </div>
        )}
        {view === "month" && (
          <div className="grid grid-cols-4 gap-2 p-3">
            {months.map((month) => (
              <Button
                key={month}
                variant="ghost"
                className="w-full bg-white text-black hover:bg-gray-100"
                onClick={() => handleMonthSelection(month)}
              >
                {format(setMonth(new Date(), month), "MMM", { locale: pt })}
              </Button>
            ))}
          </div>
        )}
        {view !== "month" && view !== "year" && (
          <Calendar
            mode="single"
            selected={datePicker}
            onSelect={handleDateChange}
            month={currentDate}
            onMonthChange={setCurrentDate}
            className="rounded-md border"
            locale={pt}
            classNames={{
              day_selected:
                "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
              day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
              day_today: "bg-accent text-accent-foreground",
              day_outside: "text-muted-foreground opacity-50",
              day_disabled: "text-muted-foreground opacity-50",
              day_range_middle:
                "aria-selected:bg-accent aria-selected:text-accent-foreground",
              day_hidden: "invisible",
              nav_button:
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse space-y-1",
              head_row: "flex",
              head_cell:
                "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
              row: "flex w-full mt-2",
              cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
              caption: "flex justify-center pt-1 relative items-center",
              caption_label: "text-sm font-medium",
              caption_dropdowns: "flex justify-center gap-1",
              dropdown: "relative",
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
